import { zodResolver } from '@hookform/resolvers/zod'
import type { LucideIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'
import { Spinner } from '@/shared/ui/spinner'

// 160 is what the column holds. Checked here as well as by the server so the
// message arrives before the round trip rather than as a toast after it.
const schema = z.object({ name: z.string().trim().min(1).max(160) })

type Values = z.infer<typeof schema>

/**
 * Naming something: a new one, or one that already has a name.
 *
 * Shared rather than written once per caller, because they differ only in which
 * mutation they hand it and what the button says. A name that validated
 * differently in the sidebar than on the page would be a bug nobody would think
 * to look for -- and naming a thing that does not exist yet is the same
 * question as renaming one that does, asked with the box empty.
 *
 * Mount it only while it is open. The current name is the form's initial value,
 * and react-hook-form reads initial values once, so a permanently mounted copy
 * would still be offering the name the thing had when the page loaded.
 */
export function NameDialog({
  open,
  onOpenChange,
  title,
  description,
  icon: Icon,
  label,
  currentName,
  submitLabel,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** A line under the title saying what is being named, when it needs one. */
  description?: string
  /** Shown beside the title, so the thing is recognised as well as read. */
  icon?: LucideIcon
  label: string
  /** Empty when the thing is being made rather than renamed. */
  currentName: string
  /** What the button says. Renaming, unless the caller says otherwise. */
  submitLabel?: string
  pending: boolean
  onSubmit: (name: string) => void
}) {
  const { t } = useTranslation()
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: currentName },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {Icon !== undefined && <Icon className="size-4 text-muted-foreground" />}
            {title}
          </DialogTitle>
          {description !== undefined && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(event) =>
              void form.handleSubmit(({ name }) => onSubmit(name))(event)
            }
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t('action.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending && <Spinner />}
                {submitLabel ?? t('action.rename')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
